import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  Box,
  Button,
  ButtonIcon,
  FooterActionBar,
  Icon,
  MobileDrawer,
  Popover,
  Typography,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { getMessageKey } from '../../../utils/utils';
import CertsModalCommentBox from '../../CertsModalCommentBox';
import { PREDEFINED_COMMENTS_FOR_ACTION } from '../constant';

import './CertsRejectAction.css';

function CertsRejectAction({
  isMobileXlargeWidth,
  // autoRevoke,
  commentStatus,
  handleClick,
  // isLoading,
  getTypeActEntRole,
  placement,
  trigger,
  isRejectPopoverOpen,
  setIsRejectPopoverOpen,
}) {
  const intl = useIntl();
  const [isCritical, setIsCritical] = useState(false);
  const [commentValue, setCommentValue] = useState('');
  const closeButtonRef = useRef(null);

  const { predefinedComments } = useSelector(
    (state) => state?.certifications?.details?.core
  );

  const { messages } = useSelector((state) => state?.user);

  const REJECT_ACTION_DATA = {
    TITLE:
      messages.CERTS_REJECT_TITLE ||
      localize(intl, getMessageKey('CERTS_REJECT_TITLE')),
    // NOt required remove while refactoring
    // DESC: 'Access won‘t be removed right away. It’ll take effect after you finish your review and submit.',
    // NOTE: 'If you don’t submit by the due date, access will be removed automatically at that time.',
    // PRIMARYBUTTON: 'Reject',
    // SECONDARYBUTTON: 'Got it',
    // CANCELBUTTON: 'Cancel',
  };

  // Reset on close
  useEffect(() => {
    if (!isRejectPopoverOpen) {
      setIsCritical(false);
      setCommentValue('');
    }
  }, [isRejectPopoverOpen]);

  useEffect(() => {
    if (isCritical) {
      setIsCritical(false);
    }
  }, [commentValue]);

  const isCommentNotValid = Boolean(
    isCritical || commentValue?.replace(/[<>]/g, '')?.trim()?.length < 3
  );

  const handleClose = () => setIsRejectPopoverOpen(false);

  const handleCommentSubmit = () => {
    if (isCommentNotValid) return setIsCritical(true);

    handleClose();

    return handleClick(commentValue);
  };

  const titleString = `${REJECT_ACTION_DATA.TITLE} ${getTypeActEntRole}?`;

  const getMobileHeader = () => (
    <Box className='CertsRejectAction-headerWrapper'>
      <Box className='CertsActionPopover-title'>
        <Icon kind='HelpFilled' className='CertsRejectAction-headerIcon' />
        <Typography kind='label' desktop='h3-d' mobile='h1-m'>
          {titleString}
        </Typography>
      </Box>
      <ButtonIcon
        ref={closeButtonRef}
        size={!isMobileXlargeWidth ? 'small' : 'medium'}
        kind='ghost'
        tabIndex={-1}
        icon={<Icon kind='Close' />}
        onClick={handleClose}
      />
    </Box>
  );

  const getMobileContent = () => (
    <CertsModalCommentBox
      defaultComments={
        predefinedComments[PREDEFINED_COMMENTS_FOR_ACTION.REJECT]
      }
      showAlert={isCritical}
      onChange={setCommentValue}
      className='CertsActionPopover-commentBox'
      label={
        messages['comments.ATTESTATIONS'] ||
        localize(intl, getMessageKey('comments.ATTESTATIONS'))
      }
      size='medium'
      allowEditPredefinedComments={
        predefinedComments?.allowEditPredefinedComments
      }
    />
  );

  const getMobileFooter = () => (
    <FooterActionBar size='large' className='CertsActionPopover-controls'>
      <Button type='button' kind='ghost' onClick={handleClose}>
        {messages.CERTS_REJECT_CANCEL_BTN ||
          localize(intl, getMessageKey('CERTS_REJECT_CANCEL_BTN'))}
      </Button>
      <Button
        type='button'
        kind={!isCommentNotValid ? 'filled' : 'outlined'}
        onClick={() => handleCommentSubmit()}
        className='CertsActionPopover-submtBtn'>
        {messages.CERTS_REJECT_PRIMARY_BTN ||
          localize(intl, getMessageKey('CERTS_REJECT_PRIMARY_BTN'))}
      </Button>
    </FooterActionBar>
  );

  // Render one time use multiple places
  const mobileHeader = getMobileHeader();
  const mobileContent = getMobileContent();
  const mobileFooter = getMobileFooter();

  const withRequiredComments = () => (
    <Box className='CertsRejectAction'>
      {mobileHeader}
      {/* TODO: Don't delete this code, might need in the future */}
      {/* <Typography className='Certs-Reject-Popover-commentsDesc' variant='h3'>{REJECT_ACTION_DATA.DESC}</Typography>{check && (<div className='CertsRejectPopover-NoteWrapper'><b className='CertsRejectPopover-NoteText'>Important: </b>{REJECT_ACTION_DATA.NOTE}</div>)}<HLine /> */}
      {mobileContent}
      <Box size='large' className='CertsActionPopover-controls'>
        <Button type='button' kind='ghost' onClick={handleClose}>
          {messages.CERTS_REJECT_CANCEL_BTN ||
            localize(intl, getMessageKey('CERTS_REJECT_CANCEL_BTN'))}
        </Button>
        <Button
          type='button'
          kind={!isCommentNotValid ? 'filled' : 'outlined'}
          onClick={() => handleCommentSubmit()}
          className='CertsActionPopover-submtBtn'>
          {messages.CERTS_REJECT_PRIMARY_BTN ||
            localize(intl, getMessageKey('CERTS_REJECT_PRIMARY_BTN'))}
        </Button>
      </Box>
    </Box>
  );

  // TODO: Don't delete this code, might need in the future
  // const withAutoRevoke = (check) => (
  //   <Box className='CertsRejectActionPopover'>
  //     <Typography variant='h3' className='CertsRejectPopover-desc'>
  //       {REJECT_ACTION_DATA.DESC}
  //     </Typography>
  //     {check && (
  //       <Box className='CertsRejectPopover-NoteWrapper CertsRejectPopover-note'>
  //         <b className='CertsRejectPopover-NoteText'>Important: </b>
  //         {REJECT_ACTION_DATA.NOTE}
  //       </Box>
  //     )}
  //     <Box tag='div' className='CertsRejectAction-controls'>
  //       <Button
  //         type='button'
  //         kind='ghost'
  //         isDisabled={isLoading}
  //         onClick={handleClose}>
  //         {REJECT_ACTION_DATA.CANCELBUTTON}
  //       </Button>
  //       <Button
  //         isDisabled={isLoading}
  //         type='button'
  //         kind='filled'
  //         onClick={() => handleClick('')}>
  //         {REJECT_ACTION_DATA.SECONDARYBUTTON}
  //       </Button>
  //     </Box>
  //   </Box>
  // );

  const getRejectActionPopover = () =>
    commentStatus ? withRequiredComments(true) : withRequiredComments(false);
  // TODO: Don't delete this code, might need in the future
  // if (autoRevoke) {return commentStatus ? withRequiredComments(true) : withAutoRevoke(true);} return commentStatus ? withRequiredComments(false) : withAutoRevoke(false);

  return (
    <Box tag='div' className='CertsRejectAction-contextWrapper'>
      {isMobileXlargeWidth ? (
        <>
          {trigger}
          <MobileDrawer
            isOpen={isRejectPopoverOpen}
            setIsOpen={setIsRejectPopoverOpen}
            closeButtonRef={closeButtonRef}
            headerContent={mobileHeader}
            content={mobileContent}
            footerActionBar={mobileFooter}
            className='CertsRejectAction'
          />
        </>
      ) : (
        <Popover
          isPortalEnabled
          placement={placement}
          trigger={trigger}
          popoverContent={getRejectActionPopover()}
          isOpen={isRejectPopoverOpen}
          setIsOpen={setIsRejectPopoverOpen}
        />
      )}
    </Box>
  );
}

CertsRejectAction.propTypes = {
  // autoRevoke: PropTypes.bool.isRequired,
  commentStatus: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
  getTypeActEntRole: PropTypes.string.isRequired,
  placement: PropTypes.string.isRequired,
  trigger: PropTypes.element.isRequired,
  // isLoading: PropTypes.bool,
  isRejectPopoverOpen: PropTypes.bool.isRequired,
  setIsRejectPopoverOpen: PropTypes.func.isRequired,
  isMobileXlargeWidth: PropTypes.bool.isRequired,
};

CertsRejectAction.defaultProps = {};

export default CertsRejectAction;
